import type { Writable } from "svelte/store";
import type { SwiftConfig } from "./fs";
import { FS } from "./fs";
import { s3SanitizeDir, s3SanitizePath } from "./fs-s3";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// OpenStack Swift answers other origins only for containers set up for it, so
// calls go through /api/swift. The user and key are request headers.

class FileSystemSwift extends FS {
  constructor(config: SwiftConfig) {
    super(config);
  }

  get swiftConfig(): SwiftConfig {
    return this.config as SwiftConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await swiftList(this.swiftConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the key headers. Fetch the
    // file and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    path = s3SanitizePath(path);
    let url = swiftURL(this.swiftConfig, "/api/swift/download", "path", path);
    let xhr = await swiftSend("GET", url, this.swiftConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    path = s3SanitizePath(path);
    let url = swiftURL(this.swiftConfig, "/api/swift/upload", "path", path);
    await swiftSend("PUT", url, this.swiftConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = swiftURL(this.swiftConfig, "/api/swift/rename", "path", path, "newPath", newPath);
    await swiftCall(this.swiftConfig, url);
  }

  async createDirectory(path: string): Promise<void> {
    let url = swiftURL(this.swiftConfig, "/api/swift/createdirectory", "path", path);
    await swiftCall(this.swiftConfig, url);
  }

  async deleteFile(path: string): Promise<void> {
    path = s3SanitizePath(path);
    let url = swiftURL(this.swiftConfig, "/api/swift/deletefile", "path", path);
    await swiftCall(this.swiftConfig, url);
  }
}

export async function testSwiftConnection(config: SwiftConfig): Promise<void> {
  await swiftCall(config, swiftURL(config, "/api/swift/test"));
}

async function swiftList(config: SwiftConfig, dir: string): Promise<any[][]> {
  dir = s3SanitizeDir(dir);
  let js = await swiftCall(config, swiftURL(config, "/api/swift/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("swift list failed");
  }
  return js;
}

export function swiftURL(config: SwiftConfig, path: string, ...args: string[]): string {
  return mkURL(
    path,
    "authUrl",
    config.authUrl,
    "auth",
    config.auth,
    "domain",
    config.domain,
    "project",
    config.project,
    "region",
    config.region,
    "container",
    config.container,
    ...args,
  );
}

function encodeSecret(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  for (let b of bytes) {
    bin += String.fromCharCode(b);
  }
  return btoa(bin);
}

export function swiftHeaders(config: SwiftConfig): Record<string, string> {
  return {
    "X-Swift-User": encodeSecret(config.user),
    "X-Swift-Key": encodeSecret(config.key),
  };
}

async function swiftCall(config: SwiftConfig, url: string): Promise<any> {
  let rsp = await fetch(url, { headers: swiftHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `swift ${rsp.status}`);
  }
  return js;
}

function swiftSend(
  method: string,
  url: string,
  config: SwiftConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  let headers = swiftHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      swiftErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`swift ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("swift request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function swiftErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `swift ${xhr.status}`;
}

export function makeFileSystemSwift(config: SwiftConfig): FileSystemSwift {
  return new FileSystemSwift(config);
}
